/**
 * Matching for the Combobox (`@/components/ui/combobox`): what typed text
 * finds, in what order, and which option it names outright. Kept in a `.ts`
 * module so it is unit-tested (vitest here runs `.ts` only).
 */

export interface ComboboxOption {
  value: string
  label: string
  /** Other text that should find this option — an ISO code, an abbreviation. */
  keywords?: string[]
  /** Shown on hover over the option, e.g. a second-language description. */
  hint?: string
}

/** Case- and accent-blind, for matching what is typed. */
const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

/**
 * The options matching `query`, best first: an exact label or keyword ("be" is
 * Belgium, "fl" Florida), then labels starting with it, then labels or keywords
 * containing it.
 */
export function rankOptions(options: ComboboxOption[], query: string): ComboboxOption[] {
  const q = fold(query)
  if (!q) return options
  const exact: ComboboxOption[] = []
  const starts: ComboboxOption[] = []
  const contains: ComboboxOption[] = []
  for (const o of options) {
    const label = fold(o.label)
    const keys = (o.keywords ?? []).map(fold)
    if (label === q || keys.includes(q)) exact.push(o)
    else if (label.startsWith(q)) starts.push(o)
    else if (label.includes(q) || keys.some(k => k.startsWith(q))) contains.push(o)
  }
  return [...exact, ...starts, ...contains]
}

/** The option typed text names outright — its label or a keyword — or null. */
export function exactOption(options: ComboboxOption[], text: string): ComboboxOption | null {
  const q = fold(text)
  if (!q) return null
  return options.find(o => fold(o.label) === q || (o.keywords ?? []).some(k => fold(k) === q)) ?? null
}
